import {Typography} from '@mui/material';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import {styled} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React from 'react';

import CustomDrawer from '../../components/CustomDrawer/CustomDrawer';
import {getOrderDetails} from '../../service/failedOrders';
import {pxToRem} from '../../utils';

const StyledTypography = styled(Typography)(({theme, isaddon}) => ({
  fontSize: isaddon === 'true' ? pxToRem(11) : pxToRem(12),
  flex: 1,
  marginRight: pxToRem(8),
}));

const StyledFlexBox = styled(Box)(({theme}) => ({
  marginLeft: pxToRem(8),
  marginRight: pxToRem(8),
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledHeaderBox = styled(Box)(({theme, boxno}) => ({
  margin: `${pxToRem(8)} 0 ${pxToRem(8)} 0`,
  padding: pxToRem(14),
  backgroundColor: theme.palette.primary[`light${boxno}`] || theme.palette.primary.light,
  borderRadius: pxToRem(4),
}));

const Order = ({orderBody}) => {
  return (
    <>
      <OrderItem items={orderBody?.store_details[0]?.products} />
      <OtherDetails orderBody={orderBody} />
    </>
  );
};

const OtherDetails = ({orderBody}) => {
  return (
    <>
      <StyledHeaderBox boxno={'3'}>
        <Typography sx={{fontSize: pxToRem(18)}}>Other Details</Typography>
      </StyledHeaderBox>

      <StyledFlexBox>
        <StyledTypography>Order Instruction:</StyledTypography>
        <StyledTypography>{orderBody?.special_instruction || ''}</StyledTypography>
        <StyledTypography>Scheduled Order:</StyledTypography>
        <StyledTypography>{orderBody?.isFutureOrder ? 'Yes' : 'No'}</StyledTypography>
      </StyledFlexBox>
      <StyledFlexBox>
        <StyledTypography>Order Type:</StyledTypography>
        <StyledTypography>{orderBody?.type || '-'}</StyledTypography>
        <StyledTypography>Delivery Type:</StyledTypography>
        <StyledTypography>{orderBody?.delivery_details?.type?.toLowerCase() || '-'}</StyledTypography>
      </StyledFlexBox>
      <StyledFlexBox>
        <StyledTypography>Delivery Fee:</StyledTypography>
        <StyledTypography>{orderBody?.store_details[0]?.deliveryFee || '-'}</StyledTypography>
        <StyledTypography>Packaging Fee:</StyledTypography>
        <StyledTypography>{orderBody?.payment?.packaging_fee || '-'}</StyledTypography>
      </StyledFlexBox>
      <StyledFlexBox>
        <StyledTypography>Payment Mode:</StyledTypography>
        <StyledTypography>{orderBody?.payment?.paymentMode || '-'}</StyledTypography>
        <StyledTypography>Discount:</StyledTypography>
        <StyledTypography>{orderBody?.promotions?.promo_discount_value || '-'}</StyledTypography>
      </StyledFlexBox>
    </>
  );
};

const OrderItem = ({items}) => {
  return (
    <>
      <StyledHeaderBox boxno={'2'}>
        <Typography sx={{fontSize: pxToRem(18)}}>Item(s) Ordered</Typography>
      </StyledHeaderBox>

      {items &&
        items.map((item, index) => {
          return (
            <Box key={`${item?.id}-${index}`}>
              <StyledFlexBox>
                <StyledTypography sx={{flex: 2}}>{item?.id}</StyledTypography>
                <StyledTypography sx={{flex: 4}}>{item?.name}</StyledTypography>
                <StyledTypography>
                  {item?.quantity || 1} X {item?.price?.toFixed(2) || 0}
                </StyledTypography>
                <StyledTypography>{(item?.quantity || 1) * (item?.price?.toFixed(2) || 0)}</StyledTypography>
              </StyledFlexBox>
              {Array.isArray(item?.addons) &&
                item.addons.length > 0 &&
                item.addons.map((addon) => {
                  return (
                    <StyledFlexBox key={`${addon?.id}-${index}`} sx={{ml: 4}}>
                      <StyledTypography isaddon={'true'} sx={{flex: 2}}>
                        {addon?.id}
                      </StyledTypography>
                      <StyledTypography isaddon={'true'} sx={{flex: 4}}>
                        {addon?.name}
                      </StyledTypography>
                      <StyledTypography isaddon={'true'}>
                        {addon?.quantity || 1} X {addon?.price?.toFixed(2) || 0}
                      </StyledTypography>
                      <StyledTypography isaddon={'true'}>
                        {(addon?.quantity || 1) * (addon?.price?.toFixed(2) || 0)}
                      </StyledTypography>
                    </StyledFlexBox>
                  );
                })}
            </Box>
          );
        })}
    </>
  );
};

const FailedOrderDrawer = ({tableKey, open, onToggleOff, selectedRow, filters}) => {
  const fetchOptions = {
    documentId: selectedRow?._id,
    filters,
  };

  const dataQuery = useQuery(['data-order-details', {fetchOptions}], () => getOrderDetails(fetchOptions), {
    refetchOnWindowFocus: false,
    retry: false,
  });

  return (
    <CustomDrawer
      tableKey={tableKey}
      open={open}
      onToggleOff={onToggleOff}
      anchor={'right'}
      title={'Order Details'}
      actionButtons={false}
    >
      {dataQuery.isLoading || dataQuery.isFetching ? (
        <LinearProgress />
      ) : dataQuery.status === 'error' ? (
        <Typography>Error!</Typography>
      ) : (
        <>
          <Box sx={{padding: pxToRem(16)}}>
            <Typography sx={{fontSize: pxToRem(20), fontWeight: 800}}>
              {selectedRow?.channelName !== selectedRow?.channelGroupName
                ? `${selectedRow?.channelGroupName}-${selectedRow?.channelName}`
                : `${selectedRow?.channelGroupName}-direct`}
              {` #${selectedRow.orderId}`}
            </Typography>
            <Typography sx={{color: (theme) => theme.palette.grey[600], fontSize: pxToRem(14)}}>
              {selectedRow.orderId !== selectedRow?.displayId ? `reference no: ${selectedRow?.displayId}` : ''}
            </Typography>
            <Typography sx={{color: (theme) => theme.palette.grey[600], fontSize: pxToRem(14)}}>
              {selectedRow?.rebelCorrelationId}
            </Typography>
            <Typography sx={{color: (theme) => theme.palette.grey[600], fontSize: pxToRem(14)}}>
              {selectedRow.timestamp ? format(new Date(selectedRow.timestamp), 'dd MMM yyyy HH:mm:ss') : '-'}
            </Typography>
          </Box>
          <Box sx={{mt: 2}}>
            <StyledHeaderBox>
              <Typography sx={{fontSize: pxToRem(18)}}>Basic Details</Typography>
            </StyledHeaderBox>

            <StyledFlexBox>
              <StyledTypography>Virtual Store Code:</StyledTypography>
              <StyledTypography sx={{flex: 2}}>{selectedRow?.partnerStoreCode || '-'}</StyledTypography>
              <StyledTypography>Brand:</StyledTypography>
              <StyledTypography sx={{flex: 2}}>{`${selectedRow?.partnerBrandCode || '-'} ${
                selectedRow?.partnerBrandName || ''
              }`}</StyledTypography>
            </StyledFlexBox>
            <StyledFlexBox>
              <StyledTypography>Channel Store Code:</StyledTypography>
              <StyledTypography sx={{flex: 2}}>{selectedRow?.channelStoreCode || '-'}</StyledTypography>
              <StyledTypography>Store:</StyledTypography>
              <StyledTypography sx={{flex: 2}}>
                {`${selectedRow?.partnerPhyStoreCode || ''} ${selectedRow?.partnerPhyStoreName || ''} ${
                  selectedRow?.city || ''
                } ${selectedRow?.countryName || ''} `}
              </StyledTypography>
            </StyledFlexBox>
            {dataQuery?.data?.data?.orderBody ? <Order orderBody={dataQuery?.data?.data?.orderBody} /> : null}
          </Box>
        </>
      )}
    </CustomDrawer>
  );
};

export default FailedOrderDrawer;
